<template>
  <div class="outreach-settings" style="padding: 1.5rem;">
    <h3 class="h3" style="margin-bottom: 1rem;">Email templates</h3>
    <p style="margin-bottom: 1.5rem; color: #666;">
      Configure the SendGrid template IDs that can be used when sending outreach invitations.
    </p>

    <div v-if="loading" class="tw-flex items-center justify-center" style="padding: 2rem;">
      <LoadingNew/>
    </div>

    <template v-else>
      <div v-if="error" class="notice notice--error" style="margin-bottom: 1rem;">
        {{ error }}
      </div>

      <div v-if="success" class="notice notice--success" style="margin-bottom: 1rem;">
        Templates saved successfully.
      </div>

      <!-- Active templates -->
      <div
        v-for="(template, index) in activeTemplates"
        :key="template._key"
        class="tw-flex items-center gap-2"
        style="margin-bottom: 0.75rem;"
      >
        <ds-input
          v-model="template.name"
          placeholder="Template name (e.g. English invitation)"
          style="flex: 1;"
        />
        <ds-input
          v-model="template.template_id"
          placeholder="SendGrid template ID (d-...)"
          style="flex: 1;"
        />
        <template v-if="template._persisted">
          <span style="font-size: 0.8125rem; color: #888; white-space: nowrap;">
            {{ usageCount(template.template_id) }} sent
          </span>
          <ds-button
            size="small"
            label="Send test email"
            variant="secondary"
            @click="openTestModal(template)"
          />
          <ds-button
            v-if="usageCount(template.template_id) === 0"
            size="small"
            icon="trash"
            label="Remove"
            variant="danger"
            @click="removeTemplate(template)"
          />
          <ds-button
            v-else
            size="small"
            label="Archive"
            variant="secondary"
            @click="archiveTemplate(template)"
          />
        </template>
        <ds-button
          v-else
          size="small"
          icon="remove"
          label="Discard"
          variant="secondary"
          @click="removeTemplate(template)"
        />
      </div>

      <div v-if="activeTemplates.length === 0" style="margin-bottom: 1rem; color: #888;">
        No active templates. Add one below.
      </div>

      <!-- Archived templates -->
      <template v-if="archivedTemplates.length > 0">
        <div style="margin-top: 1.5rem; margin-bottom: 0.5rem; font-weight: 600; font-size: 0.875rem; color: #888;">
          Archived templates
        </div>
        <div
          v-for="template in archivedTemplates"
          :key="template._key"
          class="tw-flex items-center gap-2"
          style="margin-bottom: 0.5rem; opacity: 0.65;"
        >
          <span style="flex: 1; font-size: 0.875rem;">{{ template.name || '(unnamed)' }}</span>
          <span style="flex: 1; font-size: 0.8125rem; color: #888; font-family: monospace;">{{ template.template_id }}</span>
          <span style="font-size: 0.8125rem; color: #888; white-space: nowrap;">
            {{ usageCount(template.template_id) }} sent
          </span>
          <ds-button
            size="small"
            label="Restore"
            variant="secondary"
            @click="unarchiveTemplate(template)"
          />
        </div>
      </template>

      <div class="tw-flex items-center gap-2" style="margin-top: 1rem;">
        <ds-button variant="secondary" icon="plus" label="Add template" @click="addTemplate" />
        <ds-button variant="primary" :label="saving ? 'Saving…' : 'Save templates'" :disabled="saving" @click="save" />
      </div>
    </template>

    <!-- Test email modal overlay -->
    <div
      v-if="testModal.open"
      style="position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: center; justify-content: center;"
      @click.self="closeTestModal"
    >
      <div style="background: #fff; border-radius: 6px; padding: 1.5rem; width: 100%; max-width: 420px; box-shadow: 0 8px 32px rgba(0,0,0,0.18);">
        <h4 style="margin: 0 0 0.25rem;">Send test email</h4>
        <p style="margin: 0 0 1.25rem; font-size: 0.875rem; color: #666;">
          Template: <strong>{{ testModal.template?.name || testModal.template?.template_id }}</strong>
        </p>

        <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.25rem;">
          Recipient email address
        </label>
        <ds-input
          v-model="testModal.email"
          placeholder="you@example.com"
          type="email"
          style="width: 100%; margin-bottom: 1rem;"
        />

        <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.25rem;">
          Company name
        </label>
        <ds-input
          v-model="testModal.companyName"
          placeholder="Test Company Inc."
          style="width: 100%; margin-bottom: 1.25rem;"
        />

        <div v-if="testModal.success" class="notice notice--success" style="margin-bottom: 1rem;">
          Test email sent successfully.
        </div>
        <div v-if="testModal.error" class="notice notice--error" style="margin-bottom: 1rem;">
          {{ testModal.error }}
        </div>

        <div class="tw-flex items-center gap-2" style="justify-content: flex-end;">
          <ds-button variant="secondary" label="Cancel" @click="closeTestModal" />
          <ds-button
            variant="primary"
            :label="testModal.sending ? 'Sending…' : 'Send'"
            :disabled="testModal.sending || !testModal.email || !testModal.companyName"
            @click="submitTestEmail"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { fetchOutreachConfig, saveOutreachConfig, sendTestEmail } from '../../../api/outreach'
  import { defineComponent } from 'vue'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'

  let keyCounter = 0

  export default defineComponent({
    name: 'outreach-settings',
    components: { LoadingNew },
    data () {
      return {
        templates: [],   // full list including archived; each entry has a _key for v-for stability
        usageCounts: {}, // template_id => count
        loading: false,
        saving: false,
        error: null,
        success: false,
        testModal: {
          open: false,
          template: null,
          email: '',
          companyName: '',
          sending: false,
          error: null,
          success: false,
        },
      }
    },
    computed: {
      activeTemplates () {
        return this.templates.filter(t => !t.archived)
      },
      archivedTemplates () {
        return this.templates.filter(t => !!t.archived)
      },
    },
    mounted () {
      this.fetch()
    },
    methods: {
      fetch () {
        this.loading = true
        this.error = null
        fetchOutreachConfig()
          .then((data) => {
            this.usageCounts = data.usage_counts || {}
            this.templates = (data.all_email_templates || []).map(t => ({ ...t, _persisted: true, _key: ++keyCounter }))
          })
          .catch(() => {
            this.error = 'Could not load template configuration.'
          })
          .finally(() => {
            this.loading = false
          })
      },
      usageCount (templateId) {
        return this.usageCounts[templateId] || 0
      },
      addTemplate () {
        this.templates.push({ name: '', template_id: '', archived: false, _persisted: false, _key: ++keyCounter })
      },
      removeTemplate (template) {
        this.templates.splice(this.templates.indexOf(template), 1)
      },
      archiveTemplate (template) {
        template.archived = true
      },
      unarchiveTemplate (template) {
        template.archived = false
      },
      openTestModal (template) {
        this.testModal = {
          open: true,
          template,
          email: '',
          companyName: '',
          sending: false,
          error: null,
          success: false,
        }
      },
      closeTestModal () {
        this.testModal.open = false
      },
      submitTestEmail () {
        if (!this.testModal.email || !this.testModal.companyName) return
        this.testModal.sending = true
        this.testModal.error = null
        this.testModal.success = false
        sendTestEmail({
          template_id: this.testModal.template.template_id,
          recipient_email: this.testModal.email,
          company_name: this.testModal.companyName,
        })
          .then(() => {
            this.testModal.success = true
          })
          .catch((err) => {
            this.testModal.error = err?.message || 'Failed to send test email. Please check the template configuration and try again.'
          })
          .finally(() => {
            this.testModal.sending = false
          })
      },
      save () {
        this.saving = true
        this.error = null
        this.success = false
        // Strip internal tracking fields before sending
        const payload = this.templates.map(({ _key, _persisted, ...rest }) => rest)
        saveOutreachConfig({ email_templates: payload })
          .then((data) => {
            this.usageCounts = data.usage_counts || {}
            this.templates = (data.all_email_templates || []).map(t => ({ ...t, _persisted: true, _key: ++keyCounter }))
            this.success = true
          })
          .catch((err) => {
            this.error = err?.message || 'Could not save templates. Please check the values and try again.'
          })
          .finally(() => {
            this.saving = false
          })
      },
    },
  })
</script>
